import { Box, IconButton, Chip } from '@mui/material';
import { Text } from '@qwickapps/react-framework';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PsychologyIcon from '@mui/icons-material/Psychology';

interface ExecutionStatusBarProps {
  executionStatus?: 'pending' | 'running' | 'retrying' | 'failed' | 'completed';
  elapsedTime: string | null;
  hasExecutionDetails: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
}

export default function ExecutionStatusBar({
  executionStatus,
  elapsedTime,
  hasExecutionDetails,
  expanded,
  onToggleExpand,
}: ExecutionStatusBarProps) {
  const isActiveExecution = executionStatus && ['pending', 'running', 'retrying'].includes(executionStatus);

  const getStatusChip = () => {
    if (!executionStatus) return null;

    const statusConfig = {
      pending: { label: 'Pending', color: 'default' as const },
      running: { label: 'Running', color: 'info' as const },
      retrying: { label: 'Retrying', color: 'warning' as const },
      failed: { label: 'Failed', color: 'error' as const },
      completed: { label: 'Completed', color: 'success' as const },
    };

    const config = statusConfig[executionStatus];
    if (!config) return null;

    return <Chip label={config.label} size="small" color={config.color} sx={{ height: 20 }} />;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1,
        pb: 1,
        borderBottom: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isActiveExecution && (
          <PsychologyIcon
            sx={{
              fontSize: 18,
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          />
        )}
        {getStatusChip()}
        {/* Elapsed time next to status */}
        {elapsedTime && (
          <Text variant="caption" style={{ opacity: 0.8, marginLeft: 4 }}>
            {elapsedTime}
          </Text>
        )}
      </Box>
      {hasExecutionDetails && (
        <IconButton
          size="small"
          sx={{ color: 'inherit' }}
          onClick={onToggleExpand}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      )}
    </Box>
  );
}
